import {
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import {
  CalendarOptions,
  DatesSetArg,
  EventClickArg,
  EventInput,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import { StudentCalendarService } from '../../../../core/services/academic-appointments/student-calendar.service';
import { SessionViewService } from '../../../../core/services/academic-appointments/session-view.service';
import { SessionJoinService } from '../../../../core/services/academic-appointments/session-join.service';
import {
  AcademicAppointment,
  displayTime,
  sessionEnd,
  sessionStart,
} from '../../../../core/interfaces/academic-appointment';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import { EmptyStateComponent } from '../../../ui/empty-state/empty-state.component';

import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';

/** Cadence de rafraîchissement de l'horloge interne : la minute suffit. */
const TICK_MS = 60_000;

@Component({
  selector: 'app-agenda',
  standalone: true,
  imports: [
    LocalizedDatePipe,
    CommonModule,
    TranslateModule,
    FullCalendarModule,
    ErrorStateComponent,
    EmptyStateComponent,
  ],
  templateUrl: './agenda.component.html',
  styleUrls: ['./agenda.component.css'],
})
export class AgendaComponent implements OnInit {
  private readonly calendarService = inject(StudentCalendarService);
  private readonly joinService = inject(SessionJoinService);
  readonly view = inject(SessionViewService);

  @ViewChild(FullCalendarComponent) private calendar?: FullCalendarComponent;

  readonly loading = signal(true);
  readonly errored = signal(false);
  readonly sessions = signal<AcademicAppointment[]>([]);
  readonly selectedEvent = signal<AcademicAppointment | null>(null);
  readonly periodLabel = signal('');
  readonly activeView = signal<CalendarView>('dayGridMonth');

  readonly eventCount = computed(() => this.sessions().length);

  /**
   * Horloge interne. Sans elle, « en cours » resterait figé à l'heure du rendu
   * sur un agenda laissé ouvert.
   */
  private readonly now = signal(Date.now());

  constructor() {
    const timer = setInterval(() => this.now.set(Date.now()), TICK_MS);
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }

  /** Prochaines séances, pour répondre d'un coup d'œil à « c'est quoi la suite ? ». */
  readonly upcoming = computed(() => {
    const now = new Date();
    return this.sessions()
      .filter((s) => new Date(sessionEnd(s)) >= now)
      .sort((a, b) => sessionStart(a).localeCompare(sessionStart(b)))
      .slice(0, 5);
  });

  /** Séances du jour — mises en avant au-dessus de la grille. */
  readonly todaySessions = computed(() => {
    const today = new Date().toISOString().slice(0, 10);
    return this.sessions()
      .filter((s) => (s.scheduledDate ?? '').slice(0, 10) === today)
      .sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? ''));
  });

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    locale: this.view.calendarLocale(),
    direction: this.view.isRtl() ? 'rtl' : 'ltr',
    headerToolbar: false,
    height: 'auto',
    firstDay: 1,
    dayMaxEvents: 3,
    nowIndicator: true,
    slotMinTime: '07:00:00',
    slotMaxTime: '20:00:00',
    expandRows: true,
    // Des colonnes disjointes, pas des pastilles superposées.
    //
    // Par défaut FullCalendar double la largeur de chaque colonne parallèle et
    // laisse la suivante recouvrir la précédente à moitié — le style « Google
    // Agenda ». Avec trois séances à huit heures le même jour, les titres
    // s'écrivaient les uns par-dessus les autres : plus rien n'était lisible,
    // et rien ne disait même combien il y en avait.
    slotEventOverlap: false,
    eventDisplay: 'block',
    displayEventTime: false,
    events: (info, success, failure) => this.fetchEvents(info, success, failure),
    eventClassNames: (arg) => [this.view.colorClass(arg.event.extendedProps as AcademicAppointment)],
    eventContent: (arg) => this.renderEvent(arg.event.extendedProps as AcademicAppointment),
    datesSet: (arg: DatesSetArg) => this.periodLabel.set(arg.view.title),
    eventClick: (arg: EventClickArg) =>
      this.selectedEvent.set(arg.event.extendedProps as AcademicAppointment),
  };

  ngOnInit(): void {
    // Le chargement est déclenché par FullCalendar dès le premier rendu.
  }

  private api() {
    return this.calendar?.getApi();
  }

  prev(): void { this.api()?.prev(); }
  next(): void { this.api()?.next(); }
  today(): void { this.api()?.today(); }

  switchView(view: CalendarView): void {
    this.activeView.set(view);
    this.api()?.changeView(view);
  }

  reload(): void {
    this.errored.set(false);
    this.api()?.refetchEvents();
  }

  closeDetail(): void {
    this.selectedEvent.set(null);
  }

  private fetchEvents(
    info: { startStr: string; endStr: string },
    success: (events: EventInput[]) => void,
    failure: (error: Error) => void,
  ): void {
    this.loading.set(true);
    this.errored.set(false);

    this.calendarService
      .myCalendar(info.startStr.slice(0, 10), info.endStr.slice(0, 10))
      .subscribe({
        next: (res) => {
          const list = res?.data ?? [];
          this.sessions.set(list);
          this.loading.set(false);
          success(
            list.map((s) => ({
              id: String(s.id),
              title: s.title,
              start: sessionStart(s),
              end: sessionEnd(s),
              extendedProps: s,
            })),
          );
        },
        error: (err) => {
          // Une erreur laisse l'agenda vide : aucune séance inventée.
          this.sessions.set([]);
          this.errored.set(true);
          this.loading.set(false);
          failure(err);
        },
      });
  }

  /**
   * Pastille d'une seance dans la grille.
   *
   * <p>Le nombre de lignes est decide ici, d'apres la <b>duree</b>, et non par
   * une regle de style d'apres la hauteur rendue : en vue horaire la hauteur du
   * creneau est proportionnelle a la duree, et une pastille de quatre lignes
   * dans trente minutes deborde quoi qu'on lui dise ensuite. Ce que la pastille
   * tait figure au panneau de detail.</p>
   */
  private renderEvent(s: AcademicAppointment) {
    const time = displayTime(s.startTime);
    const subject = s.subjectName ?? '';
    const roomy = (s.durationMinutes ?? 0) >= 45;

    return {
      html: `
        <div class="sess-pill">
          <span class="sess-pill__head">
            <span class="sess-pill__time">${escapeHtml(time)}</span>
            <span class="sess-pill__title">${escapeHtml(s.title)}</span>
          </span>
          ${roomy && subject ? `<span class="sess-pill__meta">${escapeHtml(subject)}</span>` : ''}
        </div>`,
    };
  }

  // ─── Helpers de gabarit ────────────────────────────────────────────

  time(value: string | null | undefined): string {
    return displayTime(value);
  }

  start(s: AcademicAppointment): string {
    return sessionStart(s);
  }

  /** Séance commencée et pas encore terminée. */
  isLive(s: AcademicAppointment): boolean {
    const t = this.now();
    return new Date(sessionStart(s)).getTime() <= t
        && new Date(sessionEnd(s)).getTime() >= t;
  }

  /** Séance dont l'heure de fin est dépassée : conservée, mais estompée. */
  isDone(s: AcademicAppointment): boolean {
    return new Date(sessionEnd(s)).getTime() < this.now();
  }

  /** La réunion s'ouvre au clic ; l'accès est journalisé côté serveur. */
  join(s: AcademicAppointment): void {
    this.joinService.openMeeting(s.id, (err) =>
      console.warn('Join session failed:', err),
      s.meetingUrl,
    );
  }
}

/** Le contenu d'événement est injecté en HTML : titres et noms doivent être neutralisés. */
function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}
